import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { dealerApi } from "../lib/api";
import { dateTime, tzs } from "../lib/format";
import { PageHead } from "../components/PageHead";
import { OrderPill } from "../components/Status";

interface OrderRow {
  id: string;
  msisdn: string | null;
  mno_code: string;
  amount_minor: number;
  commission_minor: number;
  dealer_commission_minor: number;
  status: string;
  created_at: string | null;
}

const PAGE_SIZE = 25;

const MNOS = ["vodacom", "airtel", "tigo", "helios", "ttc"];

export function Transactions() {
  const [status, setStatus] = useState("");
  const [mno, setMno] = useState("");
  const [page, setPage] = useState(0);

  const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
  if (status) params.set("status", status);
  if (mno) params.set("mno", mno);

  const orders = useQuery({
    queryKey: ["dealer-orders", status, mno, page],
    queryFn: () =>
      dealerApi.get<{ data: OrderRow[]; total: number }>(`/superdealer/v1/transactions?${params}`),
    placeholderData: keepPreviousData,
  });

  const total = orders.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <PageHead
        title="Transactions"
        description="Every airtime sale made by your agents, with your dealer commission."
      />

      <div className="admin-card">
        <div className="admin-card-header">
          <h3>Network sales</h3>
          <div className="dealer-filter-row">
            <select
              className="ev-select"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(0);
              }}
              aria-label="Filter by status"
            >
              <option value="">All statuses</option>
              <option value="SUCCESS">Success</option>
              <option value="PENDING">Pending</option>
              <option value="MANUAL_REVIEW">Manual review</option>
              <option value="FAILED">Failed</option>
            </select>
            <select
              className="ev-select"
              value={mno}
              onChange={(e) => {
                setMno(e.target.value);
                setPage(0);
              }}
              aria-label="Filter by network"
            >
              <option value="">All networks</option>
              {MNOS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="admin-table-th">Order</th>
                <th className="admin-table-th">Recipient</th>
                <th className="admin-table-th">Network</th>
                <th className="admin-table-th">Amount</th>
                <th className="admin-table-th">My commission</th>
                <th className="admin-table-th">Status</th>
                <th className="admin-table-th">Date</th>
              </tr>
            </thead>
            <tbody>
              {(orders.data?.data ?? []).map((o) => (
                <tr key={o.id} className="admin-table-row-hover">
                  <td className="admin-table-td dash-mono">{o.id}</td>
                  <td className="admin-table-td">{o.msisdn ?? "—"}</td>
                  <td className="admin-table-td">{o.mno_code}</td>
                  <td className="admin-table-td">{tzs(o.amount_minor)}</td>
                  <td className="admin-table-td">
                    {/* The snapshot is what the sale would have paid; it is only
                        credited when the provider settles SUCCESS. */}
                    {o.status === "SUCCESS" ? tzs(o.dealer_commission_minor) : "—"}
                  </td>
                  <td className="admin-table-td">
                    <OrderPill status={o.status} />
                  </td>
                  <td className="admin-table-td ev-body-micro muted">{dateTime(o.created_at)}</td>
                </tr>
              ))}
              {!orders.isLoading && !orders.data?.data.length && (
                <tr>
                  <td className="admin-table-td" colSpan={7}>
                    <div className="dash-table-empty">No transactions match these filters.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="admin-pagination">
          <span className="ev-body-micro muted">
            Page {page + 1} of {pages} · {total} transactions
          </span>
          <div className="dealer-filter-row">
            <button
              className="ev-btn ev-btn-sm ev-btn-outline"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </button>
            <button
              className="ev-btn ev-btn-sm ev-btn-outline"
              disabled={page + 1 >= pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
